import { Outlet } from '@tanstack/react-router'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteFallback } from '@/shared/components/RouteFallback'
import { useLotteryEnabled } from '@/shared/hooks/useLotteryEnabled'

/**
 * Split-screen authentication shell outside the main application layout.
 * The decorative editorial column stays hidden below `lg` and out of the heading tree.
 */
export function AuthLayout() {
  const { t } = useTranslation()
  const lotteryEnabled = useLotteryEnabled()
  const points: string[] = t('auth.editorial.points', { returnObjects: true }) as string[]

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <aside
        aria-hidden
        className="relative hidden isolate overflow-hidden bg-[linear-gradient(135deg,var(--hero-gradient-start)_0%,var(--hero-gradient-middle)_70%,var(--hero-gradient-end)_100%)] p-12 text-white lg:flex lg:flex-col lg:justify-between"
      >
        <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-white">
          {t('auth.editorial.eyebrow')}
        </p>
        <div className="relative max-w-md">
          <p className="font-display text-[clamp(2rem,3.4vw,2.75rem)] font-normal leading-[1.06] tracking-[-0.012em]">
            {t('auth.editorial.title')}
          </p>
          <p className="mt-5 text-[17px] leading-relaxed text-white">
            {t('auth.editorial.lede', { context: lotteryEnabled ? 'lottery' : undefined })}
          </p>
          {Array.isArray(points) && points.length > 0 ? (
            <ul className="mt-8 space-y-3 text-[15px] text-white">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-white" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <p className="relative text-sm text-white">{t('auth.editorial.footer')}</p>
      </aside>

      <div className="flex min-h-dvh flex-col bg-muted/40 px-4 py-8 text-foreground sm:px-6 sm:py-12 lg:justify-center">
        <div className="mx-auto w-full max-w-lg flex-1 lg:flex-none">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
